import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/media/data/Documents/code/javascript/mdxp/node_modules/gatsby-theme-docz/src/base/Layout.js";
import Arguments from 'gatsby-theme-docz/src/components/arguments.jsx';
import Deck from '../src/components/deck.jsx';
import Note from '../src/components/note.jsx';
import Step from '../src/components/step.jsx';
import Zoom from '../src/components/zoom.jsx';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2 {...{
      "id": "deck"
    }}>{`Deck`}</h2>
    <p>{`This is the main component, which creates the slide deck on your page.`}</p>
    <h3 {...{
      "id": "properties"
    }}>{`Properties`}</h3>
    <Arguments of={Deck} mdxType="Arguments" />
    <h3 {...{
      "id": "examples"
    }}>{`Examples`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import Presentation from 'my-presentation.mdx'

ReactDOM.render(
  <Deck>
    <Presentation />
  </Deck>,
  document.getElementById('root')
);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "note"
    }}>{`Note`}</h2>
    <p>{`This component can be used to add notes to certain slides, which can be used in presenter mode.`}</p>
    <h3 {...{
      "id": "properties-1"
    }}>{`Properties`}</h3>
    <Arguments mdxType="Arguments">
      {{
        name: 'start',
        type: 'number',
        desc: 'On which stepIndex to show this note.',
        default: '0'
      }}
      {{
        name: 'end',
        type: 'number',
        desc: 'Until which stepIndex to show this note. If the number is smaller or equal to zero, we start counting backwards from the last step of the slide.',
        default: '0'
      }}
      {{
        name: 'children',
        type: 'node',
        required: false
      }}
    </Arguments>
    <h3 {...{
      "id": "examples-1"
    }}>{`Examples`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-md"
      }}>{`# SLIDE 1

This is the first slide.

<Note>

These are notes that are only shown in presenter mode.  
They can help you not to forget what you need to say.

- You can also use markdown syntax inside of here!
- This will work as __intended__

</Note>

---

# SLIDE 2

<Step>

- This slide contains 3 steps
- Step 2
- Step 3

</Step>

<Note end="-1">

This note will be removed for the last step of this slide.

</Note>

<Note start="1">

This note will only show with the second step.

</Note>
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "step"
    }}>{`Step`}</h2>
    <p>{`This component can be used inside your slides, to step through certain items.
It generates a step for each of its direct children and can style them depending on their respective order and the current `}<inlineCode parentName="p">{`stepIndex`}</inlineCode>{`.
The most common use case is to make items appear or disappear in order.`}</p>
    <p>{`If this component only has one child, it will implement the stepping behaviour on it's children.
If the child component happens to be a table, it will step through its rows or columns,
depending on the `}<inlineCode parentName="p">{`useColumns`}</inlineCode>{` property.`}</p>
    <h3 {...{
      "id": "properties-2"
    }}>{`Properties`}</h3>
    <Arguments of={Step} mdxType="Arguments" />
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`NOTE`}</strong><br parentName="p"></br>{`
`}{`The styles are applied to the children according to the following rules:  `}</p>
      <ul parentName="blockquote">
        <li parentName="ul">{`All children elements get `}<inlineCode parentName="li">{`base`}</inlineCode>{` styles.`}</li>
        <li parentName="ul">{`Children whose index is greater than the stepIndex (aka they have not been stepped through), get styled with `}<inlineCode parentName="li">{`before`}</inlineCode>{`.`}</li>
        <li parentName="ul">{`Children whose index is equal to the stepIndex, get styled with `}<inlineCode parentName="li">{`current`}</inlineCode>{` if it exists.`}</li>
        <li parentName="ul">{`Children whose index is smaller or equal to the stepIndex (aka they have been stepped through), get styled with `}<inlineCode parentName="li">{`after`}</inlineCode>{`.`}</li>
      </ul>
      <p parentName="blockquote">{`When different styles get applied to a child element, they get combined using spread operators, in the following order:`}</p>
      <ol parentName="blockquote">
        <li parentName="ol"><inlineCode parentName="li">{`styles.base`}</inlineCode></li>
        <li parentName="ol"><inlineCode parentName="li">{`styles.before`}</inlineCode></li>
        <li parentName="ol"><inlineCode parentName="li">{`styles.after`}</inlineCode></li>
        <li parentName="ol"><inlineCode parentName="li">{`styles.current`}</inlineCode></li>
      </ol>
    </blockquote>
    <h3 {...{
      "id": "examples-2"
    }}>{`Examples`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-mdxp"
      }}>{`# SLIDE 1

<Step>

By wrapping your content in a \`<Step />\`, you can uncover them one by one.

Pretty neat right ?  

</Step>

---

# SLIDE 2

<Step offset={0}>

You can even mix and match different types of elements.  
Here we show a paragraph.

1. And here is a numbered list.
2. Damn, all elements show up at once
3. What could we do to solve this ?

<Step>

- Can we nest \`<Step />\` components ?
- Sure we can
- And if it only has one child, it will step through its children
- Now that's wicked!

</Step>
</Step>

---

# SLIDE 3

<Step styles={{
  base:     {transition: 'opacity 0.5s ease-in-out'},
  before:   {opacity: 0},
  after:    {opacity: 1},
}}>

- We can also apply some basic styling for the different states
- This can allow for some really modern looking slides
- People will look in awe at your beatifully crafted slide deck
- Sadly, this demo runtime makes everything flicker
- But trust me, this is not the case with real slide decks!

</Step>
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "zoom"
    }}>{`Zoom`}</h2>
    <p>{`Zoom the content inside of it to a fixed aspect ratio with css scaling.`}</p>
    <p>{`This component has not much to do with presentations, but is used throughout the core codebase and might be useful to use.`}<br parentName="p"></br>{`
`}{`One of its use cases is to wrap your presentation, so that you keep the same look of your presentation on small displays.`}</p>
    <h3 {...{
      "id": "properties-3"
    }}>{`Properties`}</h3>
    <Arguments of={Zoom} mdxType="Arguments" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      